/**
 * Sysadmin Platform rail — TanStack Query hooks for PR-B (Phase 5).
 *
 * Backs the 3 v1 sub-tabs:
 *   - /sysadmin/platform/deployment  → useSysadminDeployment + useUpdateSysadminDeployment
 *   - /sysadmin/platform/keys        → useSysadminPlatformKeys + 4 mutations
 *   - /sysadmin/platform/airgap      → useSysadminAirgap + allowlist mutations
 *
 * Built on top of `createSysadminQuery` + `createSysadminMutation` from
 * use-sysadmin-query.ts. Schemas live HERE (vs duplicated across
 * routes) so the wire types stay in lockstep with the Fastify route
 * schemas — manual sync today, codegen target in v1.1.
 *
 * Refresh cadence: defaults to 10s via createSysadminQuery so the
 * Platform rail ticks with the rest of the sysadmin surface.
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminMutation,
  createSysadminStepUpMutation,
} from './use-sysadmin-query';

// ════════════════════════════════════════════════════════════════════════
//  Deployment
// ════════════════════════════════════════════════════════════════════════

export const DeploymentModeSchema = z.enum([
  'iq_connected',
  'standalone',
  'hybrid',
]);
export type DeploymentMode = z.infer<typeof DeploymentModeSchema>;

export const DeploymentProfileSchema = z.enum([
  'cpu_only',
  'standard_gpu',
  'high_perf',
  'edge',
]);
export type DeploymentProfile = z.infer<typeof DeploymentProfileSchema>;

export const CaddyConfigSchema = z.object({
  reverseProxyEnabled: z.boolean(),
  tlsAutoRenew: z.boolean(),
  httpRedirect: z.boolean(),
  adminApiEnabled: z.boolean(),
});
export type CaddyConfig = z.infer<typeof CaddyConfigSchema>;

export const NetworkTopologySchema = z.object({
  external: z.object({
    caddyOnly: z.boolean(),
    ports: z.array(z.number().int()),
  }),
  internal: z.object({
    egressBlocked: z.boolean(),
    services: z.array(z.string()),
  }),
  iqBridge: z.object({
    enabled: z.boolean(),
    targetHost: z.string().nullable(),
  }),
});
export type NetworkTopology = z.infer<typeof NetworkTopologySchema>;

export const DeploymentConfigSchema = z.object({
  mode: DeploymentModeSchema,
  profile: DeploymentProfileSchema,
  caddyConfig: CaddyConfigSchema,
  networkTopology: NetworkTopologySchema,
  updatedBy: z.string().uuid().nullable(),
  updatedAt: z.string(),
});
export type DeploymentConfig = z.infer<typeof DeploymentConfigSchema>;

export const DeploymentPatchSchema = z
  .object({
    mode: DeploymentModeSchema.optional(),
    profile: DeploymentProfileSchema.optional(),
    caddyConfig: CaddyConfigSchema.optional(),
    networkTopology: NetworkTopologySchema.optional(),
  })
  .refine(
    (v) =>
      v.mode !== undefined ||
      v.profile !== undefined ||
      v.caddyConfig !== undefined ||
      v.networkTopology !== undefined,
    {
      message:
        'PATCH body must include at least one of: mode, profile, caddyConfig, networkTopology.',
    },
  );
export type DeploymentPatch = z.infer<typeof DeploymentPatchSchema>;

export const useSysadminDeployment = createSysadminQuery({
  key: ['sysadmin-platform-deployment'],
  url: '/admin/platform/deployment',
  schema: DeploymentConfigSchema,
});

// #1131 — Deployment PATCH is Tier-1 destructive: a mode/profile flip
// re-keys the entire platform's network topology + reverse-proxy
// wiring. X-Step-Up-Reason header forwarded automatically.
export const useUpdateSysadminDeployment = createSysadminStepUpMutation<
  DeploymentPatch,
  DeploymentConfig
>({
  url: '/admin/platform/deployment',
  method: 'PATCH',
  inputSchema: DeploymentPatchSchema,
  outputSchema: DeploymentConfigSchema,
  invalidates: [['sysadmin-platform-deployment']],
});

// ════════════════════════════════════════════════════════════════════════
//  Keys (JWT + KMS + TLS — cross-tenant browse)
// ════════════════════════════════════════════════════════════════════════

export const JwtAlgorithmSchema = z.enum(['RS256', 'ES256', 'EdDSA']);
export type JwtAlgorithm = z.infer<typeof JwtAlgorithmSchema>;

export const KmsAlgorithmSchema = z.enum(['AES256', 'AES128']);
export type KmsAlgorithm = z.infer<typeof KmsAlgorithmSchema>;

export const JwtKeyRowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationName: z.string().nullable(),
  organizationSlug: z.string().nullable(),
  kid: z.string(),
  algorithm: JwtAlgorithmSchema,
  status: z.enum(['active', 'draining', 'retired']),
  publicKeyFingerprint: z.string(),
  createdAt: z.string(),
  activatedAt: z.string().nullable(),
  retireAt: z.string().nullable(),
  retiredAt: z.string().nullable(),
});
export type JwtKeyRow = z.infer<typeof JwtKeyRowSchema>;

export const KmsKeyRowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationName: z.string().nullable(),
  organizationSlug: z.string().nullable(),
  alias: z.string(),
  algorithm: KmsAlgorithmSchema,
  status: z.enum(['active', 're_encrypting', 'retired']),
  rotationStartedAt: z.string().nullable(),
  rotationCompletedAt: z.string().nullable(),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
});
export type KmsKeyRow = z.infer<typeof KmsKeyRowSchema>;

export const TlsCertRowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationName: z.string().nullable(),
  organizationSlug: z.string().nullable(),
  name: z.string(),
  san: z.array(z.string()),
  notBefore: z.string(),
  notAfter: z.string(),
  issuer: z.string(),
  serialNumber: z.string(),
  status: z.enum(['active', 'expired', 'revoked']),
  daysToExpiry: z.number().int(),
  autoRenew: z.boolean(),
  createdAt: z.string(),
});
export type TlsCertRow = z.infer<typeof TlsCertRowSchema>;

export const KeysListResponseSchema = z.object({
  jwtKeys: z.array(JwtKeyRowSchema),
  kmsKeys: z.array(KmsKeyRowSchema),
  tlsCertificates: z.array(TlsCertRowSchema),
  summary: z.object({
    jwtActive: z.number().int(),
    jwtDraining: z.number().int(),
    kmsActive: z.number().int(),
    kmsReEncrypting: z.number().int(),
    tlsActive: z.number().int(),
    tlsExpiringSoon: z.number().int(),
    tlsExpired: z.number().int(),
  }),
});
export type KeysListResponse = z.infer<typeof KeysListResponseSchema>;

export const useSysadminPlatformKeys = createSysadminQuery({
  key: ['sysadmin-platform-keys'],
  url: '/admin/platform/keys',
  schema: KeysListResponseSchema,
});

// ─── JWT rotate ──────────────────────────────────────────────────────

export const JwtRotateInputSchema = z.object({
  organizationId: z.string().uuid(),
  algorithm: JwtAlgorithmSchema.default('RS256'),
});
export type JwtRotateInput = z.infer<typeof JwtRotateInputSchema>;

export const JwtRotateOutputSchema = z.object({
  id: z.string().uuid(),
  kid: z.string(),
  algorithm: JwtAlgorithmSchema,
  organizationId: z.string().uuid(),
  status: z.literal('active'),
});
export type JwtRotateOutput = z.infer<typeof JwtRotateOutputSchema>;

// #1131 — JWT rotation is Tier-1 destructive (see route's
// `require-fresh-totp.ts` wiring). The mutation hook accepts
// `{ input, stepUpReason }` and forwards `stepUpReason` as the
// `X-Step-Up-Reason` header. The UI dialog collects the reason via
// `StepUpReasonDialog` before firing the mutation.
export const useRotateJwtKey = createSysadminStepUpMutation<
  JwtRotateInput,
  JwtRotateOutput
>({
  url: '/admin/platform/keys/jwt/rotate',
  method: 'POST',
  inputSchema: JwtRotateInputSchema,
  outputSchema: JwtRotateOutputSchema,
  invalidates: [['sysadmin-platform-keys']],
});

// ─── JWT force-retire ────────────────────────────────────────────────

export const JwtForceRetireInputSchema = z.object({
  id: z.string().uuid(),
  confirmed: z.literal(true),
});
export type JwtForceRetireInput = z.infer<typeof JwtForceRetireInputSchema>;

const ForceRetireOutputSchema = z.object({ success: z.literal(true) });

// #1131 — JWT force-retire is Tier-1 destructive: it bypasses the
// 7-day drain window and immediately invalidates in-flight tokens.
// X-Step-Up-Reason header forwarded automatically.
export const useForceRetireJwtKey = createSysadminStepUpMutation<
  JwtForceRetireInput,
  z.infer<typeof ForceRetireOutputSchema>
>({
  url: ({ id }) => `/admin/platform/keys/jwt/${id}/force-retire`,
  method: 'POST',
  // The factory passes the FULL input as the body. The route ignores
  // `id` (it's also in the URL) and uses only `confirmed`.
  inputSchema: JwtForceRetireInputSchema,
  outputSchema: ForceRetireOutputSchema,
  invalidates: [['sysadmin-platform-keys']],
});

// ─── KMS rotate ──────────────────────────────────────────────────────

export const KmsRotateInputSchema = z.object({
  organizationId: z.string().uuid(),
  alias: z.string().min(1).max(120),
  algorithm: KmsAlgorithmSchema.default('AES256'),
});
export type KmsRotateInput = z.infer<typeof KmsRotateInputSchema>;

export const KmsRotateOutputSchema = z.object({
  newKeyId: z.string().uuid(),
  previousKeyId: z.string().uuid().nullable(),
  alias: z.string(),
  algorithm: KmsAlgorithmSchema,
  organizationId: z.string().uuid(),
});
export type KmsRotateOutput = z.infer<typeof KmsRotateOutputSchema>;

// #1131 — KMS key rotation is Tier-1 destructive: it starts a lazy
// re-encryption pass on existing data. X-Step-Up-Reason header
// forwarded automatically via the step-up mutation factory.
export const useRotateKmsKey = createSysadminStepUpMutation<
  KmsRotateInput,
  KmsRotateOutput
>({
  url: '/admin/platform/keys/kms/rotate',
  method: 'POST',
  inputSchema: KmsRotateInputSchema,
  outputSchema: KmsRotateOutputSchema,
  invalidates: [['sysadmin-platform-keys']],
});

// ─── TLS revoke ──────────────────────────────────────────────────────

export const TlsRevokeInputSchema = z.object({
  id: z.string().uuid(),
  confirmed: z.literal(true),
});
export type TlsRevokeInput = z.infer<typeof TlsRevokeInputSchema>;

const TlsRevokeOutputSchema = z.object({ success: z.literal(true) });

// #1131 — TLS revoke is Tier-1 destructive: revoking interrupts
// service traffic on the bound endpoints. X-Step-Up-Reason header
// forwarded automatically.
export const useRevokeTlsCert = createSysadminStepUpMutation<
  TlsRevokeInput,
  z.infer<typeof TlsRevokeOutputSchema>
>({
  url: ({ id }) => `/admin/platform/keys/tls/${id}`,
  method: 'DELETE',
  inputSchema: TlsRevokeInputSchema,
  outputSchema: TlsRevokeOutputSchema,
  invalidates: [['sysadmin-platform-keys']],
});

// ════════════════════════════════════════════════════════════════════════
//  Air-Gap
// ════════════════════════════════════════════════════════════════════════

export const AirgapProtocolSchema = z.enum([
  'tcp',
  'udp',
  'http',
  'https',
  'any',
]);
export type AirgapProtocol = z.infer<typeof AirgapProtocolSchema>;

export const AirgapSummarySchema = z.object({
  enabled: z.boolean(),
  lastVerifiedAt: z.string().nullable(),
  counters: z.object({
    blocksLast24h: z.number().int(),
    totalBlocks: z.number().int(),
    testBlocksLast24h: z.number().int(),
    loggedOnlyLast24h: z.number().int(),
  }),
  allowlist: z.object({
    activeCount: z.number().int(),
    totalCount: z.number().int(),
  }),
  observer: z.object({
    status: z.enum(['healthy', 'degraded', 'down']),
    lastRunAt: z.string().nullable(),
    ageMs: z.number().int().nullable(),
    heartbeatKey: z.string(),
    cadenceMs: z.number().int(),
  }),
});
export type AirgapSummary = z.infer<typeof AirgapSummarySchema>;

export const useSysadminAirgap = createSysadminQuery({
  key: ['sysadmin-platform-airgap'],
  url: '/admin/platform/airgap',
  schema: AirgapSummarySchema,
});

// ─── Air-gap posture (P2 T1 / ADR-0033 §8 D1) ────────────────────────
//
// Air-gap is an OPTION a customer selects, not this platform's fixed
// stance. Until the T1 setter landed, the only writer of
// `platform_settings.airgap.enabled` was the generic
// `PUT /v1/platform/settings` escape hatch — no step-up, no reason, no
// per-key validation — while THIS surface, which CLAUDE.md §6.3 claims
// sets the row, only read it.
//
// Tier-1 destructive: flipping the posture changes what every tenant on
// the box may create. Same step-up shape as the allowlist mutations —
// the X-Step-Up-Reason header is forwarded automatically, and the body's
// own `reason` is the per-change justification recorded in the platform
// audit row.
export const AirgapPostureInputSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().min(10).max(500),
  confirmed: z.literal(true),
});
export type AirgapPostureInput = z.infer<typeof AirgapPostureInputSchema>;

export const useSetAirgapPosture = createSysadminStepUpMutation<
  AirgapPostureInput,
  AirgapSummary
>({
  url: '/admin/platform/airgap/posture',
  method: 'PATCH',
  inputSchema: AirgapPostureInputSchema,
  // The setter answers with the re-read summary, so the tile re-renders
  // from the server's live value rather than optimistic local state.
  outputSchema: AirgapSummarySchema,
  invalidates: [
    ['sysadmin-platform-airgap'],
    ['sysadmin-platform-airgap-allowlist'],
  ],
});

export const AllowlistEntrySchema = z.object({
  id: z.string().uuid(),
  hostname: z.string(),
  port: z.number().int().nullable(),
  protocol: AirgapProtocolSchema,
  reason: z.string(),
  addedBy: z.string().uuid().nullable(),
  addedAt: z.string(),
  expiresAt: z.string().nullable(),
  isActive: z.boolean(),
});
export type AllowlistEntry = z.infer<typeof AllowlistEntrySchema>;

export const AllowlistListSchema = z.object({
  entries: z.array(AllowlistEntrySchema),
  pagination: z.object({
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
    hasMore: z.boolean(),
  }),
});
export type AllowlistList = z.infer<typeof AllowlistListSchema>;

export const useSysadminAllowlist = createSysadminQuery({
  key: ['sysadmin-platform-airgap-allowlist'],
  url: '/admin/platform/airgap/egress-allowlist',
  schema: AllowlistListSchema,
});

// ─── Add allowlist ───────────────────────────────────────────────────

export const AddAllowlistInputSchema = z.object({
  hostname: z
    .string()
    .min(1)
    .max(253)
    .regex(/^[a-zA-Z0-9._:-]+$/, {
      message:
        'hostname must contain only alphanumerics, dots, hyphens, underscores, or colons',
    }),
  port: z.number().int().min(1).max(65535).nullable().optional(),
  protocol: AirgapProtocolSchema.default('tcp'),
  reason: z.string().min(3).max(500),
  confirmed: z.literal(true),
});
export type AddAllowlistInput = z.infer<typeof AddAllowlistInputSchema>;

// #1131 — Adding an egress allowlist entry is Tier-1 destructive
// (widens the air-gap surface). X-Step-Up-Reason header forwarded
// automatically; the body's `reason` field stays separate (the body
// reason is the per-rule justification; the header reason is the
// session-level audit anchor).
export const useAddAllowlistEntry = createSysadminStepUpMutation<
  AddAllowlistInput,
  AllowlistEntry
>({
  url: '/admin/platform/airgap/egress-allowlist',
  method: 'POST',
  inputSchema: AddAllowlistInputSchema,
  outputSchema: AllowlistEntrySchema,
  invalidates: [
    ['sysadmin-platform-airgap'],
    ['sysadmin-platform-airgap-allowlist'],
  ],
});

// ─── Remove allowlist ────────────────────────────────────────────────

export const RemoveAllowlistInputSchema = z.object({
  id: z.string().uuid(),
  confirmed: z.literal(true),
});
export type RemoveAllowlistInput = z.infer<typeof RemoveAllowlistInputSchema>;

const SuccessSchema = z.object({ success: z.literal(true) });

// #1131 — Removing an allowlist entry is also Tier-1 (closes the
// hole — equally consequential as opening it).
export const useRemoveAllowlistEntry = createSysadminStepUpMutation<
  RemoveAllowlistInput,
  z.infer<typeof SuccessSchema>
>({
  url: ({ id }) => `/admin/platform/airgap/egress-allowlist/${id}`,
  method: 'DELETE',
  inputSchema: RemoveAllowlistInputSchema,
  outputSchema: SuccessSchema,
  invalidates: [
    ['sysadmin-platform-airgap'],
    ['sysadmin-platform-airgap-allowlist'],
  ],
});

// ─── Recent outbound attempts (pre-flight history) ───────────────────

export const OutboundAttemptRowSchema = z.object({
  id: z.string().uuid(),
  destination: z.string(),
  port: z.number().int().nullable(),
  protocol: z.string().nullable(),
  process: z.string().nullable(),
  reason: z.string().nullable(),
  blockedAt: z.string(),
  egressPolicyDecision: z.string(),
  isTest: z.boolean(),
});
export type OutboundAttemptRow = z.infer<typeof OutboundAttemptRowSchema>;

export const OutboundAttemptsResponseSchema = z.object({
  attempts: z.array(OutboundAttemptRowSchema),
  windowHours: z.number().int(),
});

/**
 * Fetch recent outbound attempts (block log). Used by the "Add
 * allowlist entry" pre-flight panel — operators see what has been
 * trying to leave BEFORE they decide whether to allow it. Not
 * auto-refreshed; consumers manually refetch when the hostname filter
 * changes.
 */
export function useOutboundAttempts(filter: {
  hostname?: string;
  limit?: number;
  windowHours?: number;
  enabled?: boolean;
}) {
  // Hand-rolled hook because createSysadminQuery doesn't yet support
  // params-bearing URLs. Mirrors the pattern in use-sysadmins.ts.
  const params = new URLSearchParams();
  if (filter.hostname) params.set('hostname', filter.hostname);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  if (filter.windowHours !== undefined) {
    params.set('windowHours', String(filter.windowHours));
  }
  const qs = params.toString();

  return _useSysadminOutboundAttempts(
    `/admin/platform/airgap/outbound-attempts${qs ? `?${qs}` : ''}`,
    filter,
  );
}

// Internal — separated so the cache key includes the filter object.
function _useSysadminOutboundAttempts(
  url: string,
  filter: { hostname?: string; limit?: number; windowHours?: number; enabled?: boolean },
) {
  // Re-use createSysadminQuery's internals via direct instantiation.
  // We don't memoise the hook factory because the filter changes per
  // call site and we want a fresh queryKey each time.
  const hook = createSysadminQuery({
    key: ['sysadmin-platform-airgap-attempts', filter],
    url,
    schema: OutboundAttemptsResponseSchema,
    refreshMs: false,
    enabled: filter.enabled,
  });
  return hook();
}
